import { useCallback, useMemo } from 'react';
import {
  ChecklistItemSource,
  ChecklistSectionSource,
  buildChecklistTreeFromSources,
  flattenTree,
  findNodeById,
  getNodePath,
  TreeNode,
} from 'shared/domain/checklistTree';

interface UseTreeBuilderResult {
  tree: TreeNode[];
  flatTree: TreeNode[];
  findNode: (id: string) => TreeNode | null;
  getPath: (id: string) => string[];
  nodeCount: number;
  sectionCount: number;
  itemCount: number;
}

interface UseTreeBuilderParams {
  sections: ChecklistSectionSource[];
  items: ChecklistItemSource[];
}

export function useTreeBuilder({
  sections,
  items,
}: UseTreeBuilderParams): UseTreeBuilderResult {
  const tree = useMemo(() => {
    return buildChecklistTreeFromSources({
      sections,
      items,
    });
  }, [sections, items]);

  const flatTree = useMemo(() => {
    return flattenTree(tree);
  }, [tree]);

  const findNode = useCallback((id: string) => findNodeById(tree, id), [tree]);

  const getPath = useCallback((id: string) => getNodePath(tree, id), [tree]);

  const counts = useMemo(() => {
    let sectionCount = 0;
    let itemCount = 0;

    flatTree.forEach((node) => {
      if (node.type === 'section') {
        sectionCount++;
      } else {
        itemCount++;
      }
    });

    return {
      nodeCount: flatTree.length,
      sectionCount,
      itemCount,
    };
  }, [flatTree]);

  return {
    tree,
    flatTree,
    findNode,
    getPath,
    ...counts,
  };
}
